import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const WithdrawalScheduler = () => {
  const [frequency, setFrequency] = useState('weekly');
  const [minThreshold, setMinThreshold] = useState('1000');
  const [settlementAccount, setSettlementAccount] = useState('bank-001');
  const [autoWithdrawal, setAutoWithdrawal] = useState(true);
  const [saving, setSaving] = useState(false);

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Bi-weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const accountOptions = [
    { value: 'bank-001', label: 'Chase Bank ****4532' },
    { value: 'bank-002', label: 'Wells Fargo ****7821' },
    { value: 'crypto-001', label: 'Bitcoin Wallet bc1q...' },
    { value: 'crypto-002', label: 'Ethereum Wallet 0x...' }
  ];

  const upcomingWithdrawals = [
    {
      id: 1,
      date: '2026-01-20',
      amount: 12450.00,
      account: 'Chase Bank ****4532',
      status: 'scheduled'
    },
    {
      id: 2,
      date: '2026-01-27',
      amount: 8920.00,
      account: 'Bitcoin Wallet bc1q...',
      status: 'scheduled'
    },
    {
      id: 3,
      date: '2026-02-03',
      amount: 15680.00,
      account: 'Chase Bank ****4532',
      status: 'scheduled'
    }
  ];

  const handleSave = async () => {
    setSaving(true);
    // Simulate save
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Withdrawal Scheduler</h3>
          <p className="text-sm text-muted-foreground">Configure automatic payout preferences</p>
        </div>
        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
          <Icon name="Settings" size={20} className="text-primary" />
        </div>
      </div>

      {/* Configuration Form */}
      <div className="space-y-4 mb-6">
        {/* Auto Withdrawal Toggle */}
        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-success/20 flex items-center justify-center">
              <Icon name="Zap" size={16} className="text-success" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Automatic Withdrawals</p>
              <p className="text-xs text-muted-foreground">Enable scheduled payouts</p>
            </div>
          </div>
          <button
            onClick={() => setAutoWithdrawal(!autoWithdrawal)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              autoWithdrawal ? 'bg-success' : 'bg-muted'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                autoWithdrawal ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Frequency */}
        <Select
          label="Payout Frequency"
          options={frequencyOptions}
          value={frequency}
          onChange={setFrequency}
          disabled={!autoWithdrawal}
        />

        {/* Minimum Threshold */}
        <Input
          label="Minimum Threshold"
          type="number"
          value={minThreshold}
          onChange={(e) => setMinThreshold(e?.target?.value)}
          placeholder="1000"
          description="Minimum balance required to trigger withdrawal"
          disabled={!autoWithdrawal}
        />

        {/* Settlement Account */}
        <Select
          label="Settlement Account"
          options={accountOptions}
          value={settlementAccount}
          onChange={setSettlementAccount}
          disabled={!autoWithdrawal}
        />

        {/* Save Button */}
        <Button
          fullWidth
          iconName="Save"
          onClick={handleSave}
          loading={saving}
          disabled={!autoWithdrawal}
        >
          Save Configuration
        </Button>
      </div>

      {/* Upcoming Withdrawals */}
      <div className="pt-6 border-t border-border">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-foreground">Upcoming Withdrawals</h4>
          <span className="text-xs text-muted-foreground">{upcomingWithdrawals?.length} scheduled</span>
        </div>
        <div className="space-y-3">
          {upcomingWithdrawals?.map((withdrawal) => (
            <div key={withdrawal?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg hover:bg-muted/40 transition-smooth">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-warning/20 flex items-center justify-center">
                  <Icon name="Calendar" size={16} className="text-warning" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    ${withdrawal?.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-muted-foreground">{withdrawal?.account}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium text-foreground">
                  {new Date(withdrawal?.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-warning">
                  <Icon name="Clock" size={10} />
                  Scheduled
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security Notice */}
      <div className="mt-6 p-3 bg-primary/10 border border-primary/20 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Shield" size={16} className="text-primary mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-foreground mb-1">Security Notice</p>
            <p className="text-xs text-muted-foreground">
              All withdrawal modifications require two-factor authentication. You'll receive verification alerts before processing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalScheduler;